/* Payments redesign — plan model and formatting helpers shared by the tabs and dialogs.

   The page shows three plans per account. They map onto the backend like this:
   - Full Subscription → billing_mode metered (or the retired flat `subscription`) + the
     assistant plan code. The price buys a block of included responses; overage bills on.
   - No assistant      → billing_mode metered/subscription + the no-assistant plan code.
   - On demand         → billing_mode `turn`: no monthly fee, pay per response from the wallet.
   `unlimited` is an operator-only mode and is shown read-only. */

import type { PaymentsAccountRow, PaymentsLedgerRow, TurnRateLadderTier } from '../../api/client';
import { ledgerVolume } from './volume';

export const ASSISTANT_PLAN_CODE = 'monthly_core_4_features';
export const NO_ASSISTANT_PLAN_CODE = 'monthly_core_no_assistant';
export const MIN_TOP_UP = 20;
/** Below this balance the header flags the wallet as low. */
export const LOW_WALLET_USD = 5;
/** Telegram support link shown in the top-up flow. */
export const SUPPORT_TELEGRAM_URL = 'https://t.me/OnlyMind_Manager';

export type PlanKey = 'assist' | 'none' | 'od';
export const PLAN_ORDER: PlanKey[] = ['assist', 'none', 'od'];
export const PLAN_ICON: Record<PlanKey, string> = { assist: 'zap', none: 'grid', od: 'activity' };

export function planCodeOf(plan: PlanKey) {
  return plan === 'none' ? NO_ASSISTANT_PLAN_CODE : ASSISTANT_PLAN_CODE;
}

export function isTurn(a: PaymentsAccountRow) {
  return (a.billing_mode || 'subscription') === 'turn';
}

export function isUnlimited(a: PaymentsAccountRow) {
  return a.billing_mode === 'unlimited';
}

/** The plan an account is on now. `choice` is the plan picked for an unpaid account. */
export function currentPlan(a: PaymentsAccountRow, choice?: string): PlanKey | 'unlimited' {
  if (isTurn(a)) return 'od';
  if (isUnlimited(a)) return 'unlimited';
  const code = a.subscription_active ? a.subscription_plan_code : choice || a.default_checkout_plan_code;
  return code === NO_ASSISTANT_PLAN_CODE ? 'none' : 'assist';
}

/** The plan an active subscription renews onto (set from the plan switch). */
export function renewalPlan(a: PaymentsAccountRow, choice?: string): PlanKey {
  const code = choice || a.auto_renew_plan_code || a.default_checkout_plan_code || a.subscription_plan_code;
  return code === NO_ASSISTANT_PLAN_CODE ? 'none' : 'assist';
}

export function planPriceCents(a: PaymentsAccountRow, plan: PlanKey) {
  if (plan === 'none') return a.no_assistant_price_cents || 0;
  if (plan === 'assist') return a.assistant_price_cents || 0;
  return 0;
}

/* ── Formatting ─────────────────────────────────────────────────── */
export function usd(n: number, digits = 2) {
  const sign = n < 0 ? '−' : '';
  return sign + '$' + Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}
export const usdCents = (c: number, digits = 2) => usd(c / 100, digits);
/** "$75" for whole dollars, "$75.50" otherwise. */
export const usdShort = (n: number) => usd(n, n % 1 ? 2 : 0);
export const int = (n: number) => Math.round(n).toLocaleString('en-US');
export const rate3 = (n: number) => '$' + n.toFixed(3);

export function dmy(iso?: string | null) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return [d.getDate(), d.getMonth() + 1].map((x) => String(x).padStart(2, '0')).join('/') + '/' + d.getFullYear();
}

export function initials(name: string) {
  const words = name.replace(/[^\p{L} _-]/gu, '').split(/[ _-]/).filter(Boolean).slice(0, 2);
  return (words.map((w) => w[0]!.toUpperCase()).join('') || name.charAt(0).toUpperCase() || '?');
}

export function accountName(a: { profile_name?: string | null; username?: string | null; account_id: string }) {
  return a.profile_name || a.username || a.account_id;
}

/* ── Earnings tiers (on-demand rate / subscription price ladder) ── */
export function grossLabel(v: number) {
  return '$' + v.toLocaleString('en-US');
}

export function tierRange(t: TurnRateLadderTier, under: (v: string) => string) {
  if (t.min_gross <= 0 && t.max_gross != null) return under(grossLabel(t.max_gross));
  if (t.max_gross == null) return grossLabel(t.min_gross) + '+';
  return grossLabel(t.min_gross) + ' – ' + grossLabel(Math.max(t.min_gross, t.max_gross - 1));
}

export function tierFor(ladder: TurnRateLadderTier[], gross: number | null | undefined) {
  if (gross == null || !ladder.length) return null;
  return ladder.reduce<TurnRateLadderTier | null>((cur, t) => (gross >= t.min_gross ? t : cur), ladder[0] ?? null);
}

/* ── Ledger rows → transaction list ─────────────────────────────── */
export type TxKind = 'top' | 'sub' | 'od' | 'ref' | 'other';

export interface TxView {
  id: string;
  kind: TxKind;
  icon: string;
  /** i18n key under `tx.desc` plus interpolation values. */
  descKey: string;
  descVars: Record<string, string | number>;
  accountIds: string[];
  date: string;
  amountCents: number;
  /** The Volume discount this charge was made at (ADR 0038), for the receipt line. */
  volume: ReturnType<typeof ledgerVolume>;
}

function metaIds(m: Record<string, unknown>): string[] {
  const ids = Array.isArray(m.account_ids) ? (m.account_ids as unknown[]).map(String) : [];
  if (typeof m.account_id === 'string') ids.push(m.account_id);
  return ids;
}

export function describeLedger(row: PaymentsLedgerRow): TxView {
  const m = row.metadata || {};
  const ids = metaIds(m);
  const base = { id: row.id, accountIds: ids, date: dmy(row.created_at), amountCents: row.amount_cents, descVars: {} as Record<string, string | number>, volume: ledgerVolume(m) };
  const planKey = m.plan_code === NO_ASSISTANT_PLAN_CODE ? 'none' : 'assist';
  switch (row.entry_type) {
    case 'top_up':
      return { ...base, kind: 'top', icon: 'arrow-down-left', descKey: 'topUp' };
    case 'debit_subscription':
      if (row.reference_type === 'subscription_upgrade' || m.from_plan_code) return { ...base, kind: 'sub', icon: 'zap', descKey: 'upgrade' };
      return { ...base, kind: 'sub', icon: planKey === 'none' ? 'grid' : 'zap', descKey: planKey === 'none' ? 'subNone' : 'subAssist' };
    case 'debit_subscription_auto_renew':
      return { ...base, kind: 'sub', icon: planKey === 'none' ? 'grid' : 'zap', descKey: planKey === 'none' ? 'renewNone' : 'renewAssist' };
    case 'debit_turn_billing':
      if (m.kind === 'overage') {
        return { ...base, kind: 'sub', icon: 'trending-up', descKey: 'overage', descVars: { n: int(Number(m.turns) || 0) } };
      }
      return { ...base, kind: 'od', icon: 'activity', descKey: 'onDemand' };
    case 'refund':
      return { ...base, kind: row.reference_type === 'turn_billing_refund' ? 'od' : 'other', icon: 'repeat', descKey: 'refund' };
    case 'referral_bonus':
      return { ...base, kind: 'ref', icon: 'gift', descKey: 'referralBonus' };
    case 'promo_redemption':
      return { ...base, kind: 'other', icon: 'tag', descKey: 'promo' };
    case 'fee':
      return { ...base, kind: 'other', icon: 'dollar-sign', descKey: 'fee' };
    default:
      return { ...base, kind: 'other', icon: 'dollar-sign', descKey: 'adjustment' };
  }
}
